import React from 'react';
import type { BydaResultDocument } from '@/services/bydaService';
import { isBydaPreviewablePdf } from './BydaResultPdfPreview';
import './BydaResultFileList.css';

type BydaResultFileListProps = {
  documents: BydaResultDocument[];
  loading?: boolean;
  saving?: boolean;
  onPreviewDocument: (document: BydaResultDocument) => void;
  onSaveToSharePoint?: () => void;
  formatStatus: (value?: string | null) => string;
  formatDateTime: (value?: string | null) => string;
  emptyText?: string;
  loadingText?: string;
};

function getDocumentKey(document: BydaResultDocument) {
  return `${document.sourceType}-${document.responseId || 0}-${document.remoteFileId}`;
}

function getOpenLabel(document: BydaResultDocument) {
  return isBydaPreviewablePdf(document) ? 'Preview PDF' : 'Open file';
}

export default function BydaResultFileList({
  documents,
  loading = false,
  saving = false,
  onPreviewDocument,
  onSaveToSharePoint,
  formatStatus,
  formatDateTime,
  emptyText = 'No response documents have been discovered for this enquiry yet.',
  loadingText = 'Loading response documents...',
}: BydaResultFileListProps) {
  if (loading && documents.length === 0) {
    return <div className="byda-result-file-list__state">{loadingText}</div>;
  }

  if (documents.length === 0) {
    return <div className="byda-result-file-list__state">{emptyText}</div>;
  }

  return (
    <div className="byda-result-file-list" role="table" aria-label="BYDA result files">
      <div className="byda-result-file-list__header" role="row">
        <div role="columnheader">File</div>
        <div role="columnheader">Response</div>
        <div role="columnheader">Received</div>
        <div role="columnheader">Blob</div>
        <div role="columnheader">SharePoint</div>
      </div>
      {documents.map((document) => {
        const canPreview = isBydaPreviewablePdf(document);
        const canSave = Boolean(onSaveToSharePoint)
          && document.blobStatus === 'staged'
          && !['filed', 'skipped'].includes(String(document.sharePointStatus || '').toLowerCase());

        return (
          <div className="byda-result-file-list__row" role="row" key={getDocumentKey(document)}>
            <div className="byda-result-file-list__file" role="cell">
              {document.downloadUrl ? (
                canPreview ? (
                  <button type="button" className="byda-result-file-list__file-button" onClick={() => onPreviewDocument(document)}>
                    {document.fileName}
                  </button>
                ) : (
                  <a href={document.downloadUrl} target="_blank" rel="noreferrer" className="byda-result-file-list__file-button">
                    {document.fileName}
                  </a>
                )
              ) : (
                <span>{document.fileName}</span>
              )}
              {document.downloadUrl ? (
                <span className="byda-result-file-list__hint">{getOpenLabel(document)}</span>
              ) : null}
            </div>
            <div className="byda-result-file-list__cell" role="cell">
              {document.responseId ? `#${document.responseId}` : '-'}
            </div>
            <div className="byda-result-file-list__cell" role="cell">
              {document.responseCreatedAt ? formatDateTime(document.responseCreatedAt) : '-'}
            </div>
            <div className="byda-result-file-list__cell" role="cell">
              <span className="byda-result-file-list__pill">{document.blobStatus ? formatStatus(document.blobStatus) : '-'}</span>
            </div>
            <div className="byda-result-file-list__sharepoint" role="cell">
              {document.sharePointWebUrl ? (
                <a href={document.sharePointWebUrl} target="_blank" rel="noreferrer" className="byda-result-file-list__status-link">
                  {formatStatus(document.sharePointStatus)}
                </a>
              ) : (
                <span className="byda-result-file-list__pill">{formatStatus(document.sharePointStatus)}</span>
              )}
              {canSave ? (
                <button type="button" className="byda-result-file-list__save-button" onClick={onSaveToSharePoint} disabled={saving}>
                  {saving ? 'Saving...' : 'Save'}
                </button>
              ) : null}
            </div>
          </div>
        );
      })}
    </div>
  );
}
